import CloseIcon from "@mui/icons-material/Close";
import {Alert, Box, Button, Dialog, DialogContent, IconButton, Skeleton, Stack, useMediaQuery, useTheme} from "@mui/material";
import {useTranslation} from "react-i18next";
import RefreshIconButton from "../../common/RefreshIconButton";
import {FOLIO_ALERT_SX} from "../../system/billing/folio/editor/folioStyles";
import {DigitalSigningDraft, DigitalSigningReissue, DigitalSigningStale} from "./DigitalSigningIssue";
import DigitalSigningLive from "./DigitalSigningLive";
import DigitalSigningStatusLine from "./DigitalSigningStatusLine";
import {DigitalSigningFlow} from "./useDigitalSigning";

const TITLE_ID = 'digital-signing-dialog-title';
const ICON_BUTTON_SX = {width: 40, height: 40};

const DigitalSigningDialog = ({flow}: {flow: DigitalSigningFlow}) => {
    const {t} = useTranslation();
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
    const {state, latest, busy} = flow;

    const body = () => {
        if (flow.loading) return <Skeleton variant={'rounded'} height={96}/>;
        if (flow.loadFailed) {
            return (
                <Alert
                    severity={'warning'}
                    sx={FOLIO_ALERT_SX}
                    action={<Button color={'inherit'} size={'small'} onClick={() => flow.refetch()}>{t('Retry')}</Button>}
                >
                    {t('The signature could not be loaded.')}
                </Alert>
            );
        }
        if (state.kind === 'DRAFT') {
            return <DigitalSigningDraft flow={flow}/>;
        }
        if (!latest) return null;
        if (state.kind === 'LIVE') {
            return (
                <DigitalSigningLive
                    key={latest.id}
                    signing={latest}
                    token={flow.token!}
                    busy={busy}
                    tipWithoutService={flow.tipWithoutService}
                    onVoid={() => void flow.voidLatest()}
                    onExpired={() => void flow.refetch()}
                />
            );
        }
        if (state.kind === 'LIVE_WITHOUT_TOKEN') {
            return <DigitalSigningReissue signing={latest} busy={busy} onCreate={() => void flow.create()} onVoid={() => void flow.voidLatest()}/>;
        }
        if (state.kind === 'SIGNED' && flow.stale) {
            return <DigitalSigningStale signing={latest} busy={busy} onCreate={() => void flow.create()}/>;
        }
        return <DigitalSigningStatusLine signing={latest}/>;
    };

    return (
        <Dialog
            open={flow.dialogOpen}
            onClose={flow.closeDialog}
            maxWidth={'xs'}
            fullWidth
            fullScreen={fullScreen}
            aria-labelledby={TITLE_ID}
        >
            <Stack direction={'row'} alignItems={'center'} gap={1} sx={{flexShrink: 0, minHeight: 40, px: 3, pt: 2, pb: 1}}>
                <Box component={'h2'} id={TITLE_ID} sx={{m: 0, flex: 1, minWidth: 0, fontSize: 18, lineHeight: '24px', fontWeight: 600}}>
                    {t('Signature')}
                </Box>
                <Box component={'span'} sx={{flexShrink: 0}}>
                    <RefreshIconButton onRefresh={flow.refresh} disabled={busy} sx={ICON_BUTTON_SX}/>
                </Box>
                <IconButton aria-label={t('Close')} onClick={flow.closeDialog} sx={ICON_BUTTON_SX}>
                    <CloseIcon/>
                </IconButton>
            </Stack>

            <DialogContent sx={{pt: 1}}>
                {body()}
            </DialogContent>
        </Dialog>
    );
};

export default DigitalSigningDialog;
